import { useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';

import defaultCarousel1 from '../../../../assets/landing/direct_reg_carousel_1.webp';

import classes from './BannerCarousel.module.scss';

interface BannerCarouselProps {
  carouselImages: string[];
  currentImage: string;
  currentIndex: number;
  hasMultipleImages: boolean;
  goToSlide: (index: number) => void;
  aspectRatio?: number; // width / height
}

export const BannerCarousel = ({
  carouselImages,
  currentImage,
  currentIndex,
  hasMultipleImages,
  goToSlide,
  aspectRatio = 2.5,
}: BannerCarouselProps) => {
  const { t } = useTranslation();
  const [imageLoadError, setImageLoadError] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const handlers = useSwipeable({
    onSwipedLeft: () => goToSlide((currentIndex + 1) % carouselImages.length),
    onSwipedRight: () => goToSlide((currentIndex - 1 + carouselImages.length) % carouselImages.length),
  });

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoadError(false);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn(`Failed to load carousel image: ${currentImage}`);
    setIsImageLoading(false);
    setImageLoadError(true);

    e.currentTarget.src = defaultCarousel1;
  };

  const handleDotClick = (index: number) => {
    goToSlide(index);
  };

  return (
    <div className={classes.carouselContainer} {...handlers}>
      {isImageLoading && (
        <div className={classes.imageLoading}>
          <div className={classes.loadingSpinner} />
        </div>
      )}

      <img
        alt={`Promotion banner ${currentIndex + 1}`}
        className={classNames(classes.bannerImage, {
          [classes.imageError]: imageLoadError,
          [classes.imageLoaded]: !isImageLoading,
        })}
        src={currentImage}
        style={{ aspectRatio }}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />

      {hasMultipleImages && !imageLoadError && (
        <div className={classes.carouselDots}>
          {carouselImages.map((_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              className={classNames(classes.dot, {
                [classes.active]: index === currentIndex,
              })}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
      )}

      {imageLoadError && process.env.NODE_ENV === 'development' && (
        <div className={classes.errorMessage}>{t('errors.imageFailedToLoad', { imageName: currentImage })}</div>
      )}
    </div>
  );
};
